import { Button, CustomHeader, ErrorMessage, Screen, Text } from "components"
import React, { useState } from "react"
import { Image, View } from "react-native"
import { style } from "./styles"
import { useNavigation, useRoute } from "@react-navigation/native"
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field"
import { Controller, useForm } from "react-hook-form"
import { translate } from "i18n"
import flash from "config/flash"
import Modal from "react-native-modal"
import { useStores } from "models"
import { useColor } from "app/theme/getThemes"

export const EditPinScreen = () => {
  const navigation = useNavigation<any>()
  const route = useRoute()
  const { userStore } = useStores()

  const memberCode = userStore?.memberCode

  const [isLoading, setIsLoading] = useState(false)
  const { control, handleSubmit, setValue, watch, clearErrors } = useForm({
    reValidateMode: "onSubmit",
  })

  const oldPinRef = useBlurOnFulfill({ value: watch("old_pin"), cellCount: 6 })
  const pinRef = useBlurOnFulfill({ value: watch("pin"), cellCount: 6 })
  const confirmPinRef = useBlurOnFulfill({ value: watch("confirmPin"), cellCount: 6 })

  const [oldPinProps, getOldPinCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("old_pin"),
    setValue: (e) => setValue("old_pin", e),
  })
  const [pinProps, getPinCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("pin"),
    setValue: (e) => setValue("pin", e),
  })
  const [confirmPinProps, getConfirmPinCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("confirmPin"),
    setValue: (e) => setValue("confirmPin", e),
  })

  const onSubmit = async (data: any) => {
    setIsLoading(true)

    if (data.pin !== data.confirmPin) {
      flash("error", translate("editNewPinScreen.pinNotSame"))
      setIsLoading(false)
    } else {
      await fetch(`${process.env.API_URL}/api/client/update-pin/${memberCode}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pin: data?.old_pin,
          newPin: data?.pin,
          company_code: process.env.COMPANY_CODE,
        }),
      })
        .then(async (res) => {
          setIsLoading(false)
          if (res?.ok) {
            flash("success", translate("editNewPinScreen.updateSuccess"))
            navigation.goBack()
          } else {
            flash("error", translate("editNewPinScreen.updateFailed"))
          }
        })
        .catch(() => {
          setIsLoading(false)
          flash("error", translate("editNewPinScreen.updateFailed"))
        })
    }
  }

  const { companyTheme } = useColor()

  return (
    <>
      <CustomHeader headerTitle={"Change 6-Digit PIN"} height={100} showBackButton withBalls />

      <Screen>
        <View style={style.container}>
          <Controller
            name="old_pin"
            control={control}
            rules={{
              required: translate("errors.required"),
              minLength: { value: 6, message: translate("errors.pinTooShort") },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <Text style={style.fieldTitle}>{translate("common.oldPin")}</Text>
                  <CodeField
                    autoFocus={true}
                    ref={oldPinRef}
                    {...oldPinProps}
                    onFocus={() => {
                      clearErrors("old_pin")
                    }}
                    value={value}
                    onChangeText={(e) => {
                      clearErrors("old_pin")
                      onChange(e)
                    }}
                    cellCount={6}
                    rootStyle={style.rootCodeStyle}
                    keyboardType="number-pad"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[
                          style.cell,
                          { borderColor: isFocused && companyTheme.first },
                          error?.message && style.errorCell,
                          {},
                        ]}
                        onLayout={getOldPinCellOnLayoutHandler(index)}
                      >
                        {symbol ? "•" : isFocused ? <Cursor /> : null}
                      </Text>
                    )}
                  />
                  {error?.message && (
                    <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                  )}
                </View>
              )
            }}
          />
          <Controller
            name="pin"
            control={control}
            rules={{
              required: translate("errors.required"),
              minLength: { value: 6, message: translate("errors.pinTooShort") },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <Text style={style.fieldTitle}>{translate("common.pin")}</Text>
                  <CodeField
                    ref={pinRef}
                    {...pinProps}
                    onFocus={() => {
                      clearErrors("pin")
                    }}
                    value={value}
                    onChangeText={(e) => {
                      clearErrors("pin")
                      onChange(e)
                    }}
                    cellCount={6}
                    rootStyle={style.rootCodeStyle}
                    keyboardType="number-pad"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[
                          style.cell,
                          { borderColor: isFocused && companyTheme.first },
                          error?.message && style.errorCell,
                        ]}
                        onLayout={getPinCellOnLayoutHandler(index)}
                      >
                        {symbol ? "•" : isFocused ? <Cursor /> : null}
                      </Text>
                    )}
                  />
                  {error?.message && (
                    <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                  )}
                </View>
              )
            }}
          />
          <Controller
            name="confirmPin"
            control={control}
            rules={{
              required: translate("errors.required"),
              minLength: { value: 6, message: translate("errors.pinTooShort") },
              validate: {
                value: (value) => watch("pin") === value || translate("errors.samePin"),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <View>
                  <Text style={style.fieldTitle}>{translate("common.confirmPin")}</Text>
                  <CodeField
                    ref={confirmPinRef}
                    {...confirmPinProps}
                    onFocus={() => {
                      clearErrors("confirmPin")
                    }}
                    value={value}
                    onChangeText={(e) => {
                      clearErrors("confirmPin")
                      onChange(e)
                    }}
                    cellCount={6}
                    rootStyle={style.rootCodeStyle}
                    keyboardType="number-pad"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[
                          style.cell,
                          { borderColor: isFocused && companyTheme.first },
                          error?.message && style.errorCell,
                        ]}
                        onLayout={getConfirmPinCellOnLayoutHandler(index)}
                      >
                        {symbol ? "•" : isFocused ? <Cursor /> : null}
                      </Text>
                    )}
                  />
                  {error?.message && (
                    <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                  )}
                </View>
              )
            }}
          />
        </View>
      </Screen>
      <View style={style.stickyBottomContainer}>
        <Button
          onPress={handleSubmit(onSubmit)}
          title={translate("common.save")}
          style={style.buttonContainer}
          titleStyle={style.buttonText}
          loading={isLoading}
        />
      </View>
    </>
  )
}
